import { ApiClient } from "./ApiClient";
import { RealmLower } from "../constants";
import { WorkflowExecution } from "./index";
import { ACCOUNT_ID } from "../common/AccountData";

export interface RedriveOptions {
  stateMachineArn: string;
  override: boolean;
  resume: boolean;
  redriveFailed: boolean;
  redriveAborted: boolean;
  redriveTimedOut: boolean;
  redriveRunning: boolean;
  startDate?: Date;
  endDate?: Date;
}

export interface RedriveStartResult {
  executionArn: string;
  logGroupName: string;
  logStreamName: string;
}

export interface RedriveResult {
  attemptedRedrives: number;
  successfullyLaunched: number;
  failedToLaunch: number;
  newExecutionDescriptions: WorkflowExecution[];
  redriveErrors: Array<{ parentFailedExecution: string; error: { errorMessage: string } }>;
}

export interface RedrivePollResult {
  redriveByDatesResult: RedriveResult | null;
  logEvents: Array<{ message: string; timestamp: string }>;
  workflowError: { error: string; cause: string } | null;
}

export class RedriveService {
  private api: ApiClient;

  constructor(api: ApiClient = ApiClient.getInstance()) {
    this.api = api;
  }

  buildStateMachineArn(realm: RealmLower, stateMachineName: string): string {
    const stage = this.api.getStage();
    const region = this.api.getRegionCode(realm);
    return `arn:aws:states:${region}:${ACCOUNT_ID[stage]}:stateMachine:${stateMachineName}`;
  }

  async startBatchRedrive(realm: RealmLower, options: RedriveOptions): Promise<RedriveStartResult> {
    const base = this.api.getBaseUrl("redrive", realm);
    const params = new URLSearchParams({
      stateMachineArn: options.stateMachineArn,
      override: String(options.override),
      resuming: String(options.resume),
      redriveFailed: String(options.redriveFailed),
      redriveAborted: String(options.redriveAborted),
      redriveTimedOut: String(options.redriveTimedOut),
      redriveRunning: String(options.redriveRunning),
    });
    if (options.startDate) {
      params.set("startDate", options.startDate.toISOString());
    }
    if (options.endDate) {
      params.set("endDate", options.endDate.toISOString());
    }

    return this.api.get<RedriveStartResult>(`${base}/redrive-workflows-by-dates?${params}`);
  }

  async pollBatchRedrive(realm: RealmLower, executionArn: string, logGroupName: string, logStreamName: string): Promise<RedrivePollResult> {
    const base = this.api.getBaseUrl("redrive", realm);
    const params = new URLSearchParams({ executionArn, logGroupName, logStreamName });
    return this.api.get<RedrivePollResult>(`${base}/redrive-workflows-by-dates-poller?${params}`);
  }
}
